/**
 * BulkNotificationPanel - Slide-in sheet for bulk-updating notification
 * preferences across a cohort of ECM members. Targets by population of focus,
 * staff assignment, enrollment status, or explicit member selection.
 */

import React, { useState, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  AlertCircle,
  BellOff,
  Shield,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { EscalationTierExplainer } from './EscalationTierExplainer';
import { useBulkNotificationPreferences } from '@/hooks/useEcmReminders';
import type { BulkNotificationPreferencesUpdate, MemberContactStatus } from '@/services/api/ecmReminders';

interface BulkNotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-selected member IDs from the table checkbox selection */
  selectedMemberIds?: number[];
  /** All members in the current dashboard view (for computing match counts) */
  allMembers?: MemberContactStatus[];
  /** Callback after bulk update completes */
  onUpdated?: () => void;
}

const ECM_POPULATIONS = [
  'Homelessness/At Risk of Homelessness',
  'High Utilizer (ED/Inpatient)',
  'SMI/SED',
  'Substance Use Disorder (SUD)',
  'Nursing Facility Resident',
  'At Risk of Institutionalization',
  'Children/Youth with Complex Needs',
  'Justice Involved/Reentry',
  'Pregnant/Postpartum at Risk',
  'Adults Living with Disability',
];

const ESCALATION_OPTIONS: { value: number | null | 'no_change'; label: string }[] = [
  { value: 'no_change', label: 'No change' },
  { value: null, label: 'Default' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 0, label: 'Off' },
];

type TriState = true | false | 'no_change';

export const BulkNotificationPanel: React.FC<BulkNotificationPanelProps> = ({
  isOpen,
  onClose,
  selectedMemberIds = [],
  allMembers = [],
  onUpdated,
}) => {
  const { bulkUpdate, updating } = useBulkNotificationPreferences();

  // --- Targeting state ---
  const [useSelected, setUseSelected] = useState(selectedMemberIds.length > 0);
  const [populationOfFocus, setPopulationOfFocus] = useState<string>('');
  const [staffFilter, setStaffFilter] = useState<string>('');
  const [enrollmentStatus, setEnrollmentStatus] = useState<string>('active');

  // --- Preference changes ---
  const [pauseAction, setPauseAction] = useState<TriState>('no_change');
  const [pausedReason, setPausedReason] = useState('');
  const [optOutAction, setOptOutAction] = useState<TriState>('no_change');
  const [escalationTier, setEscalationTier] = useState<number | null | 'no_change'>('no_change');
  const [smsAction, setSmsAction] = useState<TriState>('no_change');
  const [emailAction, setEmailAction] = useState<TriState>('no_change');
  const [maxPerDay, setMaxPerDay] = useState<number | ''>('');

  // Compute unique staff from members list
  const staffOptions = useMemo(() => {
    const map = new Map<number, string>();
    allMembers.forEach((m) => {
      if (m.staff_user_id && m.staff_name) {
        map.set(m.staff_user_id, m.staff_name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [allMembers]);

  // Estimate match count from local data
  const estimatedCount = useMemo(() => {
    if (useSelected && selectedMemberIds.length > 0) {
      return selectedMemberIds.length;
    }
    // Rough count from allMembers (not exact — backend may have more)
    let filtered = allMembers;
    if (staffFilter) {
      filtered = filtered.filter((m) => m.staff_user_id === Number(staffFilter));
    }
    // We can't filter by population locally since it's not in MemberContactStatus
    // Show "?" for population filter
    if (populationOfFocus) return null;
    return filtered.length;
  }, [useSelected, selectedMemberIds, allMembers, staffFilter, populationOfFocus]);

  // Check if any action is selected
  const hasAction =
    pauseAction !== 'no_change' ||
    optOutAction !== 'no_change' ||
    escalationTier !== 'no_change' ||
    smsAction !== 'no_change' ||
    emailAction !== 'no_change' ||
    (maxPerDay !== '' && maxPerDay > 0);

  // Check if any targeting is selected
  const hasTarget =
    (useSelected && selectedMemberIds.length > 0) ||
    populationOfFocus ||
    staffFilter ||
    enrollmentStatus;

  const handleApply = async () => {
    if (!hasTarget || !hasAction) return;

    const data: BulkNotificationPreferencesUpdate = {};

    // Targeting
    if (useSelected && selectedMemberIds.length > 0) {
      data.member_ids = selectedMemberIds;
    }
    if (populationOfFocus) {
      data.population_of_focus = populationOfFocus;
    }
    if (staffFilter) {
      data.staff_user_id = Number(staffFilter);
    }
    if (enrollmentStatus) {
      data.enrollment_status = enrollmentStatus;
    }

    // Actions
    if (pauseAction !== 'no_change') {
      data.notification_paused = pauseAction as boolean;
      if (pauseAction === true && pausedReason) {
        data.paused_reason = pausedReason;
      }
    }
    if (optOutAction !== 'no_change') {
      data.opted_out = optOutAction as boolean;
    }
    if (escalationTier !== 'no_change') {
      data.max_escalation_tier = escalationTier as number;
    }
    if (smsAction !== 'no_change') {
      data.sms_enabled = smsAction as boolean;
    }
    if (emailAction !== 'no_change') {
      data.email_enabled = emailAction as boolean;
    }
    if (maxPerDay !== '' && maxPerDay > 0) {
      data.max_reminders_per_day = maxPerDay as number;
    }

    const result = await bulkUpdate(data);
    if (result) {
      toast.success(result.message);
      onUpdated?.();
      onClose();
    } else {
      toast.error('Bulk update failed');
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="sm:max-w-xl w-full overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Bulk Notification Settings
          </SheetTitle>
          <SheetDescription>
            Update notification preferences for multiple ECM members at once.
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 space-y-6 pb-4">
          {/* ── Targeting ── */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Who
            </h3>

            {/* Pre-selected members */}
            {selectedMemberIds.length > 0 && (
              <div className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={useSelected}
                    onChange={(e) => setUseSelected(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-blue-700 dark:text-blue-300 font-medium">
                    Selected members ({selectedMemberIds.length})
                  </span>
                </label>
                {useSelected && (
                  <button
                    type="button"
                    onClick={() => setUseSelected(false)}
                    className="text-blue-400 hover:text-blue-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            {/* Population of focus */}
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                Population of Focus
              </label>
              <select
                value={populationOfFocus}
                onChange={(e) => setPopulationOfFocus(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All populations</option>
                {ECM_POPULATIONS.map((pop) => (
                  <option key={pop} value={pop}>
                    {pop}
                  </option>
                ))}
              </select>
            </div>

            {/* Staff filter */}
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                Assigned Staff
              </label>
              <select
                value={staffFilter}
                onChange={(e) => setStaffFilter(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All case managers</option>
                {staffOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Enrollment status */}
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                Enrollment Status
              </label>
              <select
                value={enrollmentStatus}
                onChange={(e) => setEnrollmentStatus(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="pending">Pending</option>
                <option value="">All statuses</option>
              </select>
            </div>

            {/* Match count */}
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600 dark:text-gray-400">
                {estimatedCount !== null ? (
                  <>
                    <span className="font-bold text-gray-900 dark:text-gray-100">
                      {estimatedCount}
                    </span>{' '}
                    members match
                  </>
                ) : (
                  <span className="text-gray-400">
                    Match count will be determined on apply
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* ── Actions ── */}
          <div className="space-y-4 border-t pt-4 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              What to change
            </h3>

            {/* Pause toggle */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BellOff className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Pause All</span>
                </div>
                <select
                  value={String(pauseAction)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPauseAction(v === 'true' ? true : v === 'false' ? false : 'no_change');
                  }}
                  className="px-2 py-1 border rounded text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600"
                >
                  <option value="no_change">No change</option>
                  <option value="true">Pause</option>
                  <option value="false">Resume</option>
                </select>
              </div>
              {pauseAction === true && (
                <input
                  type="text"
                  value={pausedReason}
                  onChange={(e) => setPausedReason(e.target.value)}
                  placeholder="Reason (e.g., Program review)..."
                  className="w-full px-3 py-1.5 border rounded-md text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                  maxLength={100}
                />
              )}
            </div>

            {/* Opt out toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Opt Out</span>
              </div>
              <select
                value={String(optOutAction)}
                onChange={(e) => {
                  const v = e.target.value;
                  setOptOutAction(v === 'true' ? true : v === 'false' ? false : 'no_change');
                }}
                className="px-2 py-1 border rounded text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600"
              >
                <option value="no_change">No change</option>
                <option value="true">Opt out</option>
                <option value="false">Re-enable</option>
              </select>
            </div>

            {/* Escalation cap */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Escalation Cap</span>
              </div>
              <div className="flex gap-1">
                {ESCALATION_OPTIONS.map((opt) => {
                  const isSelected =
                    (opt.value === 'no_change' && escalationTier === 'no_change') ||
                    opt.value === escalationTier;
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => setEscalationTier(opt.value)}
                      className={`flex-1 px-1.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                        isSelected
                          ? opt.value === 0
                            ? 'border-red-400 bg-red-50 text-red-700 dark:bg-red-900/30 dark:border-red-600 dark:text-red-300'
                            : opt.value === 'no_change'
                            ? 'border-gray-400 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:border-gray-500 dark:text-gray-300'
                            : 'border-blue-400 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {escalationTier !== 'no_change' && (
                <EscalationTierExplainer memberCap={escalationTier} compact />
              )}
            </div>

            {/* Channel toggles */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">SMS</span>
                <select
                  value={String(smsAction)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSmsAction(v === 'true' ? true : v === 'false' ? false : 'no_change');
                  }}
                  className="px-2 py-1 border rounded text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600"
                >
                  <option value="no_change">No change</option>
                  <option value="true">Enable</option>
                  <option value="false">Disable</option>
                </select>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Email</span>
                <select
                  value={String(emailAction)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setEmailAction(v === 'true' ? true : v === 'false' ? false : 'no_change');
                  }}
                  className="px-2 py-1 border rounded text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600"
                >
                  <option value="no_change">No change</option>
                  <option value="true">Enable</option>
                  <option value="false">Disable</option>
                </select>
              </div>
            </div>

            {/* Max per day */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Max reminders/day</span>
              <input
                type="number"
                value={maxPerDay}
                onChange={(e) => setMaxPerDay(e.target.value ? Number(e.target.value) : '')}
                placeholder="No change"
                min={1}
                max={50}
                className="w-24 px-2 py-1 border rounded text-xs text-right bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Warning for destructive actions */}
          {(pauseAction === true || optOutAction === true) && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {optOutAction === true
                  ? 'Opting out will permanently disable notifications and cancel all pending reminders for the matched members.'
                  : 'Pausing will cancel all pending reminders for the matched members. Reminders will resume when unpaused.'}
              </p>
            </div>
          )}
        </div>

        <SheetFooter className="border-t dark:border-gray-700">
          <div className="flex gap-2 w-full">
            <Button variant="outline" onClick={onClose} disabled={updating} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={updating || !hasTarget || !hasAction}
              className="flex-1"
            >
              {updating
                ? 'Applying...'
                : `Apply to ${estimatedCount !== null ? `${estimatedCount} members` : 'members'}`}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
